import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listDemoProgress } from "../lib/api";
import { useApp } from "../context/AppContext";
import { fmtDuration, relTime } from "../lib/format";
import { Avatar } from "../components/Avatar";

const outcomeChip = (o) => {
  if (o === "completada") return { bg: "rgba(114,40,200,0.08)", color: "#7228C8", label: "Completada" };
  if (o === "incidencia") return { bg: "rgba(30,25,36,0.06)", color: "#1E1924", label: "Con incidencia" };
  return { bg: "rgba(30,25,36,0.04)", color: "#70667A", label: "Pendiente" };
};

const groupByDate = (sessions) => {
  const groups = {};
  for (const s of sessions) {
    const d = new Date(s.created_at);
    const today = new Date();
    const diffDays = Math.floor((today - d) / (1000 * 60 * 60 * 24));
    let key;
    if (diffDays === 0) key = "Hoy";
    else if (diffDays === 1) key = "Ayer";
    else if (diffDays < 7) key = "Esta semana";
    else if (diffDays < 30) key = "Este mes";
    else key = "Antes";
    if (!groups[key]) groups[key] = [];
    groups[key].push(s);
  }
  return groups;
};

export default function DashboardPage() {
  const { state } = useApp();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadSessions = useCallback(async () => {
    try {
      const data = await listDemoProgress();
      setSessions(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const sessionMetrics = useMemo(() => {
    const totalSeconds = sessions.reduce((sum, session) => sum + session.duration_seconds, 0);
    const pronunciation = sessions.length
      ? Math.round(
        sessions.reduce((sum, session) => sum + session.pronunciation_score, 0) / sessions.length,
      )
      : 0;
    const fluency = sessions.length
      ? Math.round(sessions.reduce((sum, session) => sum + session.fluency_score, 0) / sessions.length)
      : 0;

    return {
      avgFluency: fluency,
      avgPronunciation: pronunciation,
      groups: groupByDate(sessions),
      totalSeconds,
    };
  }, [sessions]);

  const { avgFluency, avgPronunciation, groups, totalSeconds } = sessionMetrics;
  const order = ["Hoy", "Ayer", "Esta semana", "Este mes", "Antes"];

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
      <div className="wk-eyebrow mb-4">Mi progreso</div>
      <h1 className="text-[42px] lg:text-[56px] font-extrabold tracking-hero leading-[1.05]" style={{ color: "#1E1924" }}>
        Lo que has hablado.<br />Al segundo.
      </h1>

      {/* KPIs */}
      <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
        {[
          { id: "spoken-time", label: "Tiempo hablado", value: fmtDuration(totalSeconds), sub: `${sessions.length} clase${sessions.length === 1 ? "" : "s"}`, accent: false },
          { id: "trial-pack", label: "Pack de prueba", value: "$22", sub: "dos sesiones de 30 min", accent: true },
          { id: "fluency", label: "Fluidez media", value: `${avgFluency}`, sub: "sobre 100", accent: false },
          { id: "pronunciation", label: "Pronunciación", value: `${avgPronunciation}`, sub: "sobre 100", accent: false },
        ].map((k) => (
          <div key={k.id} className="wk-card p-6">
            <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "#70667A" }}>{k.label}</div>
            <div className="font-mono-num text-[24px] sm:text-[32px] lg:text-[36px] font-extrabold tracking-hero leading-none mt-2" style={{ color: k.accent ? "#7228C8" : "#1E1924" }}>
              {k.value}
            </div>
            <div className="text-[12px] mt-2" style={{ color: "#70667A" }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div className="mt-16 grid lg:grid-cols-[1.5fr_1fr] gap-10">
        {/* Historial */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-[24px] font-bold tracking-hero" style={{ color: "#1E1924" }}>Historial de clases</h2>
            <Link to="/reservar" className="text-[13px] hover:underline" style={{ color: "#7228C8" }} data-testid="dashboard-new-class-link">
              Reservar sesión →
            </Link>
          </div>

          {loading && (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => <div key={i} className="wk-skel" style={{ height: 88 }} />)}
            </div>
          )}

          {!loading && sessions.length === 0 && (
            <div className="wk-card p-12 text-center">
            <div className="mx-auto w-16 h-16 rounded-2xl mb-6" style={{ background: "rgba(114,40,200,0.08)", border: "1px dashed #7228C8" }} />
              <h3 className="text-[22px] font-bold tracking-hero" style={{ color: "#1E1924" }}>
                Aún no has hablado ni un segundo.
              </h3>
              <p className="mt-3 text-[14px] max-w-prose-tight mx-auto" style={{ color: "#70667A" }}>
                Tu inglés sigue atascado en donde lo dejaste. Elige un tutor, arranca la llamada y arregla eso.
              </p>
              <Link to="/tutores" className="wk-btn wk-btn-primary mt-6 inline-flex" data-testid="dashboard-empty-cta">
                Ir al catálogo
              </Link>
            </div>
          )}

          {!loading && sessions.length > 0 && (
            <div className="space-y-8">
              {order.filter((k) => groups[k]).map((k) => (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-[0.14em] mb-3" style={{ color: "#70667A" }}>{k}</div>
                  <div className="space-y-2.5">
                    {groups[k].map((s) => {
                      const chip = outcomeChip(s.outcome);
                      return (
                        <div key={s.id} className="wk-card p-4 flex items-center gap-4" data-testid={`session-row-${s.id}`}>
                          <Avatar initials={s.tutor_display_name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()} color="#7228C8" size={44} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="font-semibold text-[15px] truncate" style={{ color: "#1E1924" }}>{s.tutor_display_name}</div>
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-medium uppercase" style={{ background: chip.bg, color: chip.color }}>
                                {chip.label}
                              </span>
                            </div>
                            <div className="text-[12px] mt-1" style={{ color: "#70667A" }}>
                              {s.tutor_country} · {s.tutor_accent} · {relTime(s.created_at)}
                            </div>
                          </div>
                          <div className="text-right hidden sm:block">
                            <div className="font-mono-num text-[13px]" style={{ color: "#1E1924" }}>{fmtDuration(s.duration_seconds)}</div>
                            <div className="text-[11px]" style={{ color: "#70667A" }}>Fluidez {s.fluency_score} · Pron {s.pronunciation_score}</div>
                          </div>
                          <div className="font-mono-num text-[16px] font-bold tabular-nums" style={{ color: "#7228C8" }}>
                            $22
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="wk-card p-6">
            <div className="wk-eyebrow mb-3">Pack de prueba</div>
            <div className="font-mono-num text-[42px] font-extrabold tracking-hero leading-none" style={{ color: "#1E1924" }}>
              $22
            </div>
            <Link to="/reservar" className="wk-btn wk-btn-primary w-full mt-5" data-testid="dashboard-topup-btn">
              Reservar mi primera sesión · $22
            </Link>
          </div>

          <div className="wk-card p-6" style={{ background: "#1E1924", color: "#FAF9FB", border: "1px solid #1E1924" }}>
            <div className="wk-eyebrow" style={{ color: "rgba(250,249,251,0.55)" }}>Recordatorio simulado</div>
            <div className="text-[15px] mt-3 leading-[1.5]">
              Llevas 4 días sin practicar. La /ð/ no se arregla sola. ¿Retomamos con James O.?
            </div>
            <p className="text-[11px] mt-3" style={{ color: "rgba(250,249,251,0.5)" }}>
              En Fase 1, este aviso se envía por push/email cuando la IA detecta inactividad.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
